import { create } from 'zustand';

/**
 * App-wide text size (spec 07 VISNING). Device-local, not synced. Scales the
 * root font-size so every rem-based size grows together; px touch targets and
 * the map stay fixed. Applied before first paint via initTextScale().
 */
export type TextScale = 'small' | 'normal' | 'large' | 'xlarge';

const KEY = 'los.textscale';

/** Root font-size percentage per step (browser default is 100% ≈ 16px). */
const PERCENT: Record<TextScale, number> = {
  small: 90,
  normal: 100,
  large: 112,
  xlarge: 125,
};

function load(): TextScale {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'small' || saved === 'normal' || saved === 'large' || saved === 'xlarge') {
      return saved;
    }
  } catch {
    /* private mode */
  }
  return 'normal';
}

function apply(scale: TextScale): void {
  document.documentElement.style.fontSize = `${PERCENT[scale]}%`;
}

interface TextScaleState {
  scale: TextScale;
  setScale: (s: TextScale) => void;
}

export const useTextScaleStore = create<TextScaleState>((set) => ({
  scale: load(),
  setScale: (scale) => {
    try {
      localStorage.setItem(KEY, scale);
    } catch {
      /* ignore */
    }
    apply(scale);
    set({ scale });
  },
}));

/** Apply the persisted text scale before first paint. */
export function initTextScale(): void {
  apply(useTextScaleStore.getState().scale);
}
